import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import useAuth from "@/hooks/useAuth";
import { getSubscription } from "@/shared/api/pref-storage";
import type { Subscription } from "./types";

export type SubscriptionQuery = UseQueryResult<Subscription | undefined, Error>;

export const SUBSCRIPTION_QUERY_KEY = ["subscription"];

/**
 * Fetches the user's subscription record.
 *
 * The API returns 404 when no subscription exists — we treat that as a
 * successful empty result (`data: undefined`) rather than an error.
 */
export const useSubscriptionQuery = (): SubscriptionQuery => {
  const { authenticated } = useAuth();

  return useQuery<Subscription>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await getSubscription();
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (
        isAxiosError(error) &&
        error.response &&
        error.response.status < 500
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
};
